<template>
  <mdb-container fluid>
    <!--Section: Intro-->
    <section id="introduction">
      <!--Title-->
      <h2 class="font-weight-bold mt-5"><strong>Code</strong></h2>
      <!--Description-->
      <p class="description">
        Styles for inline code snippets and longer, multiline blocks of code.
      </p>
    </section>
    <!--/Section: Intro-->
    <hr class="my-5" />
    <!--Section: -->
    <section id="inline-code">
      <!--Title-->
      <h4 class="mb-3 dark-grey-text font-weight-bold">
        <strong>Inline code</strong>
      </h4>
      <!--Description-->
      <p class="description">
        Wrap inline snippets of code with <code>&lt;code&gt;</code>. Be sure to
        escape HTML angle brackets.
      </p>
      <!--Section: Live preview-->
      <section>
        <p>
          For example, <code>&lt;section&gt;</code> should be wrapped as inline.
        </p>
      </section>
      <!--Section: Live preview-->

      <!--Section: Code-->
      <section>
        <pre class="code-toolbar">
          <code class="language-markup">
            For example, &lt;code&gt;&amp;lt;section&amp;gt;&lt;/code&gt; should be wrapped as inline.
          </code>
        </pre>
      </section>
      <!--Section: Code-->
    </section>
    <!--/Section: -->
    <hr class="my-5" />
    <!--Section: -->
    <section id="code-blocks">
      <!--Title-->
      <h4 class="mb-3 dark-grey-text font-weight-bold">
        <strong>Code blocks</strong>
      </h4>
      <!--Description-->
      <p class="description">
        Use <code>&lt;pre&gt;</code>s for multiple lines of code. Once again, be
        sure to escape any angle brackets in the code for proper rendering. You
        may optionally add the <code>.pre-scrollable</code> class, which will
        set a max-height of 350px and provide a y-axis scrollbar.
      </p>

      <!--Section: Live preview-->
      <section>
        <pre>
          <code>&lt;p&gt;Sample text here...&lt;/p&gt;
                    &lt;p&gt;And another line of sample text here...&lt;/p&gt;
          </code>
        </pre>
      </section>
      <!--Section: Live preview-->

      <!--Section: Code-->
      <section>
        <pre class="code-toolbar">
          <code class="language-markup">
            &lt;pre&gt;&lt;code&gt;&amp;lt;p&amp;gt;Sample text here...&amp;lt;/p&amp;gt;
            &amp;lt;p&amp;gt;And another line of sample text here...&amp;lt;/p&amp;gt;
            &lt;/code&gt;&lt;/pre&gt;
          </code>
        </pre>
      </section>
      <!--Section: Code-->
    </section>
    <!--/Section: -->
    <hr class="my-5" />
    <!--Section: -->
    <section id="variables">
      <!--Title-->
      <h4 class="mb-3 dark-grey-text font-weight-bold">
        <strong>Variables</strong>
      </h4>
      <!--Description-->
      <p class="description">
        For indicating variables use the <code>&lt;var&gt;</code> tag.
      </p>
      <!--Section: Live preview-->
      <section><var>y</var> = <var>m</var><var>x</var> + <var>b</var></section>
      <!--Section: Live preview-->

      <!--Section: Code-->
      <section>
        <pre class="code-toolbar">
          <code class="language-markup">
            &lt;var&gt;y&lt;/var&gt; = &lt;var&gt;m&lt;/var&gt;&lt;var&gt;x&lt;/var&gt; + &lt;var&gt;b&lt;/var&gt;
          </code>
        </pre>
      </section>
      <!--Section: Code-->
    </section>
    <!--/Section: -->
    <hr class="my-5" />
    <!--Section: -->
    <section id="user-input">
      <!--Title-->
      <h4 class="mb-3 dark-grey-text font-weight-bold">
        <strong>User input</strong>
      </h4>
      <!--Description-->
      <p class="description">
        Use the <code>&lt;kbd&gt;</code> to indicate input that is typically
        entered via keyboard.
      </p>
      <!--Section: Live preview-->
      <section>
        <p>
          To switch directories, type <kbd>cd</kbd> followed by the name of the
          directory.
        </p>

        <p>
          To edit settings, press <kbd><kbd>ctrl</kbd> + <kbd>,</kbd></kbd>
        </p>
      </section>
      <!--Section: Live preview-->

      <!--Section: Code-->
      <section>
        <pre class="code-toolbar">
            <code class="language-markup">
              To switch directories, type &lt;kbd&gt;cd&lt;/kbd&gt; followed by the name of the directory.&lt;br&gt;
              To edit settings, press &lt;kbd&gt;&lt;kbd&gt;ctrl&lt;/kbd&gt; + &lt;kbd&gt;,&lt;/kbd&gt;&lt;/kbd&gt;
            </code>
          </pre>
      </section>
      <!--Section: Code-->
    </section>
    <!--/Section: -->
    <hr class="my-5" />
    <!--Section: -->
    <section id="sample-output" class="pb-3">
      <!--Title-->
      <h4 class="mb-3 dark-grey-text font-weight-bold">
        <strong>Sample output</strong>
      </h4>
      <!--Description-->
      <p class="description">
        For indicating sample output from a program use the
        <code>samp</code> tag.
      </p>
      <!--Section: Live preview-->
      <section>
        <samp
          >This text is meant to be treated as sample output from a computer
          program.</samp
        >
      </section>
      <!--Section: Live preview-->

      <!--Section: Code-->
      <section>
        <pre class="code-toolbar">
          <code class="language-markup">
            <samp>This text is meant to be treated as sample output from a computer program.</samp>
          </code>
        </pre>
      </section>
      <!--Section: Code-->
    </section>
    <!--/Section: -->

    <!--Section: Docs link-->
    <section class="pb-4 mt-5">
      <!--Panel-->
      <mdb-card>
        <mdb-card-header class="primary-color text-center white-text"
          >Full documentation</mdb-card-header
        >
        <mdb-card-body class="text-center">
          <p>Read the full documentation for these components.</p>
          <a
            href="https://mdbootstrap.com/docs/vue/content/code/"
            target="_blank"
            class="btn btn-primary"
            >Learn more</a
          >
        </mdb-card-body>
      </mdb-card>
      <!--/.Panel-->
    </section>
    <!--Section: Docs link-->
  </mdb-container>
</template>

<script>
import { mdbContainer, mdbCard, mdbCardHeader, mdbCardBody } from "mdbvue";

export default {
  name: "Code",
  components: {
    mdbContainer,
    mdbCard,
    mdbCardHeader,
    mdbCardBody
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.code-toolbar {
  background-color: #fff;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12);
}
</style>
